<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isSubmitting || isLoadingRoutes || isDuplicating" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="rm-badge-section">إنشاء رحلات</span>
          <div v-if="duplicateSourceRef" class="rm-duplicate-pill d-inline-flex align-center">
            <v-icon x-small color="#0284C7" class="me-1">mdi-content-copy</v-icon>
            <span>نسخ من الرحلة: <strong>{{ duplicateSourceRef }}</strong></span>
          </div>
          <div class="rm-breadcrumb">
            <router-link to="/trips" class="rm-bc-link">إدارة الرحلات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="rm-bc-item rm-bc-active">إنشاء وجدولة رحلات جديدة</span>
          </div>
        </div>
        <h1 class="rm-main-title">إنشاء وجدولة رحلات المسار</h1>
        <p class="rm-subtitle">حدد المسار وأيام التشغيل ومواعيد الانطلاق ليتم توليد الرحلات واحتساب توقيتات المحطات تلقائياً</p>
      </div>

      <div class="rm-header-actions">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="goBack"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          إلغاء والعودة
        </v-btn>
      </div>
    </div>

    <!-- ==================== 2-COLUMN LAYOUT ==================== -->
    <div class="rm-create-grid">
      <!-- ==================== COLUMN 1: FORM (65%) ==================== -->
      <div class="rm-form-col">
        <v-form ref="tripForm" v-model="isFormValid" @submit.prevent="submitBatchTrips">
          <!-- Section 1: Route Selection -->
          <div class="rm-card mb-6">
            <div class="rm-card-header d-flex align-center mb-4">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-routes</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">1. اختيار المسار التشغيلي</h2>
                <p class="rm-card-subtitle">حدد المسار الذي سيتم ربط الرحلات الجديدة به</p>
              </div>
            </div>

            <v-autocomplete
              v-model="form.route_id"
              :items="routesList"
              item-text="displayName"
              item-value="id"
              :filter="filterRoutes"
              label="اختر المسار *"
              outlined
              dense
              :rules="[v => !!v || 'يرجى اختيار المسار أولاً']"
              placeholder="ابحث باسم المسار أو كوده..."
              @change="onRouteSelected"
              class="rm-custom-input"
            >
              <template v-slot:item="{ item }">
                <div class="d-flex align-center justify-space-between w-100 py-1">
                  <div>
                    <span class="font-weight-bold text-navy me-2">{{ item.name }}</span>
                    <span class="rm-stops-pill">{{ item.stops_count || (item.stops ? item.stops.length : 0) }} محطات</span>
                  </div>
                  <span class="rm-code-badge">{{ item.route_code || ('R-' + String(item.id).padStart(4, '0')) }}</span>
                </div>
              </template>
            </v-autocomplete>
          </div>

          <!-- Section 2: Operating Days -->
          <div class="rm-card mb-6">
            <div class="rm-card-header d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
              <div class="d-flex align-center">
                <div class="rm-icon-header-box me-3">
                  <v-icon size="20" color="#082C69">mdi-calendar-week</v-icon>
                </div>
                <div>
                  <h2 class="rm-card-title">2. أيام العمل الأسبوعية</h2>
                  <p class="rm-card-subtitle">حدد الأيام التي سيتم تشغيل الرحلات فيها أسبوعياً</p>
                </div>
              </div>

              <!-- Quick Select Buttons -->
              <div class="d-flex align-center gap-1">
                <button
                  type="button"
                  class="rm-quick-btn"
                  @click="selectAllDays"
                >
                  طوال الأسبوع (يوميًا)
                </button>
                <button
                  type="button"
                  class="rm-quick-btn"
                  @click="selectWorkdays"
                >
                  الأحد - الخميس
                </button>
                <button
                  type="button"
                  class="rm-quick-btn btn-clear"
                  @click="clearAllDays"
                >
                  مسح
                </button>
              </div>
            </div>

            <!-- Weekday Checkboxes -->
            <div class="rm-weekdays-grid">
              <label
                v-for="day in weekdayOptions"
                :key="day.id"
                class="rm-weekday-item"
                :class="{ 'item-checked': form.operating_days.includes(day.label) }"
              >
                <input
                  type="checkbox"
                  :value="day.label"
                  v-model="form.operating_days"
                  class="rm-checkbox"
                />
                <span class="rm-day-text">{{ day.label }}</span>
              </label>
            </div>

            <div v-if="form.operating_days.length === 0" class="rm-error-hint mt-2">
              <v-icon x-small color="#EF4444" class="me-1">mdi-alert-circle</v-icon>
              <span>يرجى اختيار يوم واحد على الأقل للتشغيل</span>
            </div>
          </div>

          <!-- Section 3: Timing and Intervals -->
          <div class="rm-card mb-6">
            <div class="rm-card-header d-flex align-center mb-4">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-clock-time-four-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">3. أوقات الانطلاق والتكرار اليومي</h2>
                <p class="rm-card-subtitle">حدد مواعيد البداية والنهاية والفاصل الزمني بالدقائق لتوليد الرحلات</p>
              </div>
            </div>

            <div class="row">
              <!-- First Trip Time -->
              <div class="col-12 col-md-4 py-0">
                <label class="rm-input-label">موعد أول رحلة *</label>
                <v-text-field
                  v-model="form.first_stop_time"
                  type="time"
                  outlined
                  dense
                  :rules="[v => !!v || 'يرجى تحديد وقت أول رحلة']"
                  class="rm-custom-input"
                ></v-text-field>
              </div>

              <!-- Last Trip Time -->
              <div class="col-12 col-md-4 py-0">
                <label class="rm-input-label">موعد آخر رحلة *</label>
                <v-text-field
                  v-model="form.last_stop_time"
                  type="time"
                  outlined
                  dense
                  :rules="[v => !!v || 'يرجى تحديد وقت آخر رحلة']"
                  class="rm-custom-input"
                ></v-text-field>
              </div>

              <!-- Interval (Minutes) -->
              <div class="col-12 col-md-4 py-0">
                <label class="rm-input-label">الفاصل بين الرحلات (بالدقائق)</label>
                <v-text-field
                  v-model.number="form.interval_minutes"
                  type="number"
                  min="0"
                  step="5"
                  outlined
                  dense
                  placeholder="مثال: 30"
                  class="rm-custom-input"
                  hint="ضع 0 لإنشاء رحلة واحدة فقط في موعد أول رحلة"
                  persistent-hint
                ></v-text-field>
              </div>
            </div>
          </div>

          <!-- Section 4: Operational Parameters -->
          <div class="rm-card mb-6">
            <div class="rm-card-header d-flex align-center mb-4">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-cog-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">4. إعدادات تشغيلية إضافية</h2>
                <p class="rm-card-subtitle">تاريخ بدء السريان وأوقات الانتظار بالمحطات</p>
              </div>
            </div>

            <div class="row">
              <!-- Effective Date -->
              <div class="col-12 col-md-6 py-0">
                <label class="rm-input-label">تاريخ بدء سريان الجدول</label>
                <v-text-field
                  v-model="form.effective_date"
                  type="date"
                  outlined
                  dense
                  class="rm-custom-input"
                  hint="التاريخ الذي يبدأ منه تطبيق الجدولة (افتراضياً: اليوم)"
                  persistent-hint
                ></v-text-field>
              </div>

              <!-- Station Wait Minutes -->
              <div class="col-12 col-md-6 py-0">
                <label class="rm-input-label">وقت انتظار الركاب في المحطة (دقائق)</label>
                <v-text-field
                  v-model.number="form.station_wait_minutes"
                  type="number"
                  min="0"
                  max="30"
                  outlined
                  dense
                  class="rm-custom-input"
                  hint="الوقت المخصص لصعود ونزول الركاب (الافتراضي: 5 دقائق)"
                  persistent-hint
                ></v-text-field>
              </div>
            </div>
          </div>

          <!-- Section 5: Subscription Controls -->
          <div class="rm-card mb-6">
            <div class="rm-card-header d-flex align-center mb-4">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-card-account-details-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">5. خيارات الاشتراك التجاري</h2>
                <p class="rm-card-subtitle">تحديد أهلية مقاعد الرحلة لباقات واشتراكات الركاب</p>
              </div>
            </div>

            <div class="d-flex flex-column gap-3">
              <!-- Toggle 1: Available for Subscription -->
              <div
                class="rm-toggle-row"
                :class="{ 'is-active': form.available_for_subscription }"
                @click="toggleAvailableForSubscription"
              >
                <div class="rm-toggle-switch">
                  <div class="rm-toggle-track">
                    <div class="rm-toggle-thumb"></div>
                  </div>
                </div>
                <div class="rm-toggle-text">
                  <span class="rm-toggle-title">متاحة لاشتراكات الركاب</span>
                  <span class="rm-toggle-desc">السماح لعملاء الباقات بحجز مقاعد مجدولة على متن هذه الرحلة</span>
                </div>
              </div>

              <!-- Toggle 2: Subscription Only -->
              <div
                class="rm-toggle-row"
                :class="{
                  'is-active': form.subscription_only,
                  'is-disabled': !form.available_for_subscription
                }"
                @click="toggleSubscriptionOnly"
              >
                <div class="rm-toggle-switch">
                  <div class="rm-toggle-track">
                    <div class="rm-toggle-thumb"></div>
                  </div>
                </div>
                <div class="rm-toggle-text">
                  <span class="rm-toggle-title">حصرية للمشتركين فقط</span>
                  <span class="rm-toggle-desc">حظر الحجوزات الفردية العادية وقصر الرحلة على المشتركين</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex align-center justify-end gap-3 pt-2">
            <v-btn
              depressed
              class="rm-btn-secondary"
              @click="goBack"
            >
              إلغاء
            </v-btn>

            <v-btn
              type="button"
              depressed
              class="rm-btn-primary rm-btn-submit"
              :disabled="isSubmitting"
              :loading="isSubmitting"
              @click.prevent="submitBatchTrips"
            >
              <template v-if="!isSubmitting">
                <v-icon right size="18" class="ms-1">mdi-check-all</v-icon>
                توليد وحفظ الرحلات ({{ generatedDepartureTimes.length }})
              </template>
              <template v-else>
                <v-progress-circular indeterminate size="18" width="2" color="white" class="ms-2" />
                جاري إنشاء الرحلات...
              </template>
            </v-btn>
          </div>
        </v-form>
      </div>

      <!-- ==================== COLUMN 2: LIVE PREVIEW (35%) ==================== -->
      <div class="rm-preview-col">
        <div class="rm-sticky-panel">
          <div class="rm-card rm-preview-card">
            <!-- Header -->
            <div class="rm-preview-header d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon size="22" color="#082C69" class="me-2">mdi-eye-outline</v-icon>
                <h3 class="rm-preview-title">معاينة الرحلات المتولدة</h3>
              </div>
              <span class="rm-count-badge">
                {{ generatedDepartureTimes.length }} {{ generatedDepartureTimes.length === 1 ? 'رحلة' : 'رحلات' }}
              </span>
            </div>

            <!-- Route Details Preview -->
            <div v-if="selectedRoute" class="rm-preview-box mb-4">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="rm-box-label">المسار المختار</span>
                <span class="rm-code-badge">{{ selectedRoute.route_code || ('R-' + String(selectedRoute.id).padStart(4, '0')) }}</span>
              </div>
              <div class="font-weight-bold text-navy mb-2">{{ selectedRoute.name }}</div>

              <div class="d-flex align-center flex-wrap gap-2 mb-2">
                <span class="rm-stat-pill">
                  <v-icon x-small color="#082C69" class="me-1">mdi-bus-stop</v-icon>
                  {{ selectedRouteStopsCount }} محطات
                </span>
                <span v-if="selectedRoute.distance" class="rm-stat-pill">
                  <v-icon x-small color="#082C69" class="me-1">mdi-map-marker-distance</v-icon>
                  {{ selectedRoute.distance }} كم
                </span>
                <span v-if="selectedRoute.approximate_duration" class="rm-stat-pill">
                  <v-icon x-small color="#082C69" class="me-1">mdi-clock-time-four-outline</v-icon>
                  {{ selectedRoute.approximate_duration }}
                </span>
              </div>

              <div v-if="selectedRoute.start_stop_name && selectedRoute.end_stop_name" class="rm-terminal-strip d-flex align-center justify-space-between">
                <span class="text-truncate">{{ selectedRoute.start_stop_name }}</span>
                <v-icon x-small color="#64748B" class="mx-1 flex-shrink-0">mdi-arrow-left</v-icon>
                <span class="text-truncate">{{ selectedRoute.end_stop_name }}</span>
              </div>
            </div>

            <div v-else class="rm-preview-empty-box mb-4 text-center py-4">
              <v-icon small color="#94A3B8" class="mb-1">mdi-routes</v-icon>
              <div class="text-caption text--secondary">لم يتم اختيار مسار بعد</div>
            </div>

            <!-- Operating Days Preview -->
            <div class="rm-preview-box mb-4">
              <span class="rm-box-label mb-2 d-block">أيام التشغيل المحددة</span>
              <div v-if="form.operating_days.length > 0" class="d-flex flex-wrap gap-1">
                <span
                  v-for="d in form.operating_days"
                  :key="d"
                  class="rm-days-tag"
                >
                  {{ d }}
                </span>
              </div>
              <div v-else class="text-caption text-error">
                لم يتم اختيار أي أيام تشغيل
              </div>
            </div>

            <!-- Generated Departure Times -->
            <div class="rm-preview-box mb-4">
              <span class="rm-box-label mb-2 d-block">مواعيد انطلاق الرحلات اليومية</span>

              <div v-if="generatedDepartureTimes.length > 0" class="rm-times-list">
                <div
                  v-for="(time, idx) in generatedDepartureTimes"
                  :key="idx"
                  class="rm-time-item d-flex align-center justify-space-between"
                >
                  <span class="rm-time-index">رحلة #{{ idx + 1 }}</span>
                  <span class="rm-time-val font-weight-bold text-navy">
                    <v-icon x-small color="#082C69" class="me-1">mdi-clock-outline</v-icon>
                    {{ formatTime(time) }}
                  </span>
                </div>
              </div>

              <div v-else class="text-caption text--secondary py-2 text-center">
                حدد موعد أول رحلة لتوليد المواعيد
              </div>
            </div>

            <!-- Technical Note -->
            <div class="rm-tech-note">
              <v-icon x-small color="#082C69" class="me-1">mdi-information-outline</v-icon>
              <span>
                سيتم إنشاء رحلات رئيسية (Parent Trips) في جدول <code>trips</code>، واحتساب مواعيد المحطات بدقة باستخدام خدمة التوقيت المعتمدة لمسافة Google، ومزامنة تشغيلها وفق الأيام المختارة.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";

export default {
  name: "RouteMakingCreateTrips",
  components: {
    VueElementLoading,
  },
  data() {
    return {
      isSubmitting: false,
      isLoadingRoutes: false,
      isDuplicating: false,
      duplicateSourceRef: null,
      isFormValid: true,
      routesList: [],
      selectedRoute: null,
      weekdayOptions: [
        { id: "sat", label: "السبت" },
        { id: "sun", label: "الأحد" },
        { id: "mon", label: "الاثنين" },
        { id: "tue", label: "الثلاثاء" },
        { id: "wed", label: "الأربعاء" },
        { id: "thu", label: "الخميس" },
        { id: "fri", label: "الجمعة" },
      ],
      form: {
        route_id: null,
        operating_days: ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"],
        first_stop_time: "06:00",
        last_stop_time: "08:00",
        interval_minutes: 30,
        effective_date: new Date().toISOString().substr(0, 10),
        station_wait_minutes: 5,
        stop_to_stop_avg_time: null,
        available_for_subscription: true,
        subscription_only: false,
      },
    };
  },
  computed: {
    canSubmit() {
      return (
        this.form.route_id &&
        this.form.operating_days.length > 0 &&
        this.form.first_stop_time &&
        this.form.last_stop_time &&
        this.generatedDepartureTimes.length > 0 &&
        !this.isSubmitting
      );
    },
    selectedRouteStopsCount() {
      if (!this.selectedRoute) return 0;
      if (this.selectedRoute.stops_count) return this.selectedRoute.stops_count;
      if (this.selectedRoute.stops) return this.selectedRoute.stops.length;
      return 0;
    },
    generatedDepartureTimes() {
      const first = this.form.first_stop_time;
      const last = this.form.last_stop_time;
      const interval = parseInt(this.form.interval_minutes, 10) || 0;

      if (!first) return [];

      const fParts = first.split(":");
      const fMins = parseInt(fParts[0], 10) * 60 + parseInt(fParts[1] || 0, 10);

      if (!last || interval <= 0) {
        return [first];
      }

      const lParts = last.split(":");
      const lMins = parseInt(lParts[0], 10) * 60 + parseInt(lParts[1] || 0, 10);

      if (lMins <= fMins) {
        return [first];
      }

      const times = [];
      for (let m = fMins; m <= lMins; m += interval) {
        const hrs = Math.floor(m / 60) % 24;
        const mins = m % 60;
        times.push(`${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`);
      }

      return times;
    },
  },
  created() {
    this.fetchRoutes();
  },
  methods: {
    fetchRoutes() {
      this.isLoadingRoutes = true;
      axios
        .get("/routes/all")
        .then((response) => {
          const raw = response.data || [];
          this.routesList = raw.map((r) => {
            const code = r.route_code || `R-${String(r.id).padStart(4, "0")}`;
            return {
              ...r,
              displayName: `${code} - ${r.name}`,
            };
          });

          // Check if duplicate_from is passed in query
          const dupId = this.$route.query.duplicate_from;
          if (dupId) {
            this.loadDuplicateData(dupId);
          } else {
            // Check if route_id is passed in query
            const qRouteId = this.$route.query.route_id;
            if (qRouteId) {
              const found = this.routesList.find((r) => String(r.id) === String(qRouteId));
              if (found) {
                this.form.route_id = found.id;
                this.onRouteSelected(found.id);
              }
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching routes:", err);
          this.notifyUser("error", "خطأ", "تعذر تحميل قائمة المسارات");
        })
        .finally(() => {
          this.isLoadingRoutes = false;
        });
    },
    loadDuplicateData(dupId) {
      this.isDuplicating = true;
      axios
        .get(`/trips/${dupId}/parent-details`)
        .then((res) => {
          if (res.data && res.data.trip) {
            const t = res.data.trip;
            this.duplicateSourceRef = t.trip_reference || `T-${String(t.id).padStart(4, "0")}`;
            if (t.route_id) {
              this.form.route_id = t.route_id;
              this.onRouteSelected(t.route_id);
            }
            if (t.operating_days && Array.isArray(t.operating_days) && t.operating_days.length > 0) {
              this.form.operating_days = [...t.operating_days];
            }
            if (t.first_stop_time) {
              const fTime = t.first_stop_time.substring(0, 5);
              this.form.first_stop_time = fTime;
              this.form.last_stop_time = fTime;
            }
            if (t.station_wait_minutes !== undefined && t.station_wait_minutes !== null) {
              this.form.station_wait_minutes = t.station_wait_minutes;
            }
            if (t.stop_to_stop_avg_time !== undefined && t.stop_to_stop_avg_time !== null) {
              this.form.stop_to_stop_avg_time = t.stop_to_stop_avg_time;
            }
            if (t.effective_date) {
              this.form.effective_date = t.effective_date;
            }
            if (t.available_for_subscription !== undefined) {
              this.form.available_for_subscription = Boolean(t.available_for_subscription);
            }
            if (t.subscription_only !== undefined) {
              this.form.subscription_only = Boolean(t.subscription_only);
            }
            if (!this.form.available_for_subscription) {
              this.form.subscription_only = false;
            }
          }
        })
        .catch((err) => {
          console.error("Error loading duplicate trip source:", err);
          this.notifyUser("error", "خطأ", "تعذر تحميل بيانات الرحلة المراد نسخها");
        })
        .finally(() => {
          this.isDuplicating = false;
        });
    },
    filterRoutes(item, queryText, itemText) {
      if (!queryText) return true;
      const q = queryText.toLowerCase().trim();
      const name = (item.name || "").toLowerCase();
      const code = (item.route_code || `R-${String(item.id).padStart(4, "0")}`).toLowerCase();
      const display = (itemText || "").toLowerCase();
      return name.includes(q) || code.includes(q) || display.includes(q);
    },
    onRouteSelected(routeId) {
      this.selectedRoute = this.routesList.find((r) => r.id === routeId) || null;
      if (routeId) {
        axios
          .get(`/routes/${routeId}`)
          .then((res) => {
            if (res.data) {
              const rData = res.data;
              const stops = rData.stops || [];
              this.selectedRoute = {
                ...(this.selectedRoute || {}),
                ...rData,
                distance: rData.distance || null,
                approximate_duration: rData.approximate_duration || null,
                stops_count: stops.length || (this.selectedRoute ? this.selectedRoute.stops_count : 0),
                start_stop_name: stops.length > 0 ? (stops[0].name || stops[0].stop_name || "") : "",
                end_stop_name: stops.length > 1 ? (stops[stops.length - 1].name || stops[stops.length - 1].stop_name || "") : "",
              };
            }
          })
          .catch((err) => {
            console.error("Error fetching detailed route info:", err);
          });
      }
    },
    selectAllDays() {
      this.form.operating_days = this.weekdayOptions.map((w) => w.label);
    },
    selectWorkdays() {
      this.form.operating_days = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
    },
    clearAllDays() {
      this.form.operating_days = [];
    },
    formatTime(timeStr) {
      if (!timeStr) return "—";
      const parts = timeStr.split(":");
      if (parts.length < 2) return timeStr;
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "م" : "ص";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    },
    goBack() {
      if (this.form.route_id) {
        this.$router.push(`/trips/route/${this.form.route_id}`);
      } else {
        this.$router.push("/trips");
      }
    },
    toggleAvailableForSubscription() {
      this.form.available_for_subscription = !this.form.available_for_subscription;
      if (!this.form.available_for_subscription) {
        this.form.subscription_only = false;
      }
    },
    toggleSubscriptionOnly() {
      if (!this.form.available_for_subscription) return;
      this.form.subscription_only = !this.form.subscription_only;
    },
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : type === "warn" ? "تنبيه" : "خطأ"),
          text: text,
          type: type === "warn" ? "warn" : type,
        });
      } else if (this.$swal) {
        this.$swal(title || (type === "success" ? "تم بنجاح" : "خطأ"), text, type === "warn" ? "warning" : type);
      } else {
        alert(text);
      }
    },
    submitBatchTrips() {
      if (this.isSubmitting) return;

      if (!this.form.route_id) {
        this.notifyUser("warn", "تنبيه", "يرجى اختيار المسار");
        return;
      }

      if (!this.form.operating_days || this.form.operating_days.length === 0) {
        this.notifyUser("warn", "تنبيه", "يرجى اختيار يوم تشغيل واحد على الأقل");
        return;
      }

      if (!this.form.first_stop_time) {
        this.notifyUser("warn", "تنبيه", "يرجى تحديد موعد أول رحلة");
        return;
      }

      if (!this.form.last_stop_time) {
        this.notifyUser("warn", "تنبيه", "يرجى تحديد موعد آخر رحلة");
        return;
      }

      if (!this.form.effective_date) {
        this.notifyUser("warn", "تنبيه", "يرجى تحديد تاريخ بدء الجدول");
        return;
      }

      if (!this.generatedDepartureTimes || this.generatedDepartureTimes.length === 0) {
        this.notifyUser("warn", "تنبيه", "تعذر توليد مواعيد الرحلات، يرجى مراجعة وقت البداية والنهاية والفاصل");
        return;
      }

      if (this.$refs.tripForm && typeof this.$refs.tripForm.validate === "function") {
        const isValid = this.$refs.tripForm.validate();
        if (!isValid) {
          this.notifyUser("error", "خطأ في البيانات", "يرجى تصحيح الأخطاء في النموذج قبل الحفظ");
          return;
        }
      }

      this.isSubmitting = true;
      const payload = {
        route_id: this.form.route_id,
        operating_days: this.form.operating_days,
        first_stop_time: this.form.first_stop_time,
        last_stop_time: this.form.last_stop_time,
        interval_minutes: Number(this.form.interval_minutes) || 0,
        effective_date: this.form.effective_date,
        station_wait_minutes: Number(this.form.station_wait_minutes) || 0,
        stop_to_stop_avg_time: this.form.stop_to_stop_avg_time ? Number(this.form.stop_to_stop_avg_time) : null,
        available_for_subscription: Boolean(this.form.available_for_subscription),
        subscription_only: Boolean(this.form.subscription_only),
      };

      axios
        .post("/trips/create-batch", payload)
        .then((response) => {
          if (response.data && response.data.success) {
            this.notifyUser("success", "تم بنجاح", response.data.message || "تم إنشاء وجدولة الرحلات بنجاح");
            const targetRouteId = this.form.route_id;
            this.$router.push(`/trips/route/${targetRouteId}`);
          } else {
            const msg = response.data?.message || "حدث خطأ أثناء حفظ الرحلات";
            this.notifyUser("error", "فشل الحفظ", msg);
          }
        })
        .catch((error) => {
          console.error("Error creating batch trips:", error);
          const errData = error.response?.data;
          let msg = "حدث خطأ أثناء إنشاء الرحلات";
          if (errData?.errors) {
            const firstErr = Object.values(errData.errors)[0];
            msg = Array.isArray(firstErr) ? firstErr[0] : String(firstErr);
          } else if (errData?.message) {
            msg = errData.message;
          }
          this.notifyUser("error", "خطأ في الطلب", msg);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-flex;
  align-items: center;
  background: #E8EEF5;
  color: #082C69;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
}

.rm-duplicate-pill {
  display: inline-flex;
  align-items: center;
  background: #E0F2FE;
  color: #0369A1;
  border: 1px solid #BAE6FD;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
}

.rm-breadcrumb {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  color: #64748B;
}

.rm-bc-link {
  color: #64748B;
  text-decoration: none;
  transition: color 0.2s;
}

.rm-bc-link:hover {
  color: #082C69;
}

.rm-bc-active {
  color: #082C69;
  font-weight: 600;
}

.rm-main-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 0.875rem;
  color: #64748B;
  margin: 0;
}

/* Buttons */
.rm-btn-primary {
  background: #082C69 !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 44px !important;
  border-radius: 10px !important;
  padding: 0 20px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px rgba(8, 44, 105, 0.25) !important;
  transition: all 0.2s ease !important;
}

.rm-btn-primary:hover {
  background: #061B40 !important;
  box-shadow: 0 6px 18px rgba(8, 44, 105, 0.35) !important;
  transform: translateY(-1px);
}

.rm-btn-secondary {
  background: #FFFFFF !important;
  color: #475569 !important;
  border: 1px solid #CBD5E1 !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  height: 44px !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
}

.rm-btn-secondary:hover {
  background: #F8FAFC !important;
  border-color: #94A3B8 !important;
  color: #082C69 !important;
}

.rm-btn-submit {
  height: 44px !important;
  padding: 0 24px !important;
}

/* 2-Column Grid */
.rm-create-grid {
  display: grid;
  grid-template-columns: 65% 35%;
  gap: 20px;
  align-items: start;
}

@media (max-width: 1024px) {
  .rm-create-grid {
    grid-template-columns: 1fr;
  }
}

/* Card */
.rm-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-icon-header-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #EFF6FF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 0.775rem;
  color: #64748B;
  margin: 0;
}

.rm-input-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

/* Quick Select Buttons */
.rm-quick-btn {
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.rm-quick-btn:hover {
  background: #E2E8F0;
  color: #082C69;
}

.rm-quick-btn.btn-clear {
  color: #EF4444;
}

/* Weekday Checkboxes Grid */
.rm-weekdays-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
}

@media (max-width: 768px) {
  .rm-weekdays-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.rm-weekday-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 4px;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  background: #F8FAFC;
  cursor: pointer;
  transition: all 0.2s;
}

.rm-weekday-item:hover {
  border-color: #94A3B8;
}

.rm-weekday-item.item-checked {
  background: #EFF6FF;
  border-color: #082C69;
}

.rm-weekday-item.item-checked .rm-day-text {
  color: #082C69;
  font-weight: 700;
}

.rm-checkbox {
  margin-bottom: 6px;
  accent-color: #082C69;
  cursor: pointer;
}

.rm-day-text {
  font-size: 0.775rem;
  color: #475569;
}

.rm-error-hint {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #EF4444;
  font-weight: 600;
}

/* Preview Panel */
.rm-sticky-panel {
  position: sticky;
  top: 20px;
}

.rm-preview-card {
  border: 1.5px solid #CBD5E1;
  background: #F8FAFC;
}

.rm-preview-title {
  font-size: 1rem;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-count-badge {
  background: #082C69;
  color: #FFFFFF;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 9999px;
}

.rm-preview-box {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 12px 14px;
}

.rm-preview-empty-box {
  background: #FFFFFF;
  border: 1px dashed #CBD5E1;
  border-radius: 10px;
}

.rm-box-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748B;
}

.rm-times-list {
  max-height: 220px;
  overflow-y: auto;
  padding-left: 4px;
}

.rm-time-item {
  padding: 6px 0;
  border-bottom: 1px dashed #F1F5F9;
}

.rm-time-item:last-child {
  border-bottom: none;
}

.rm-time-index {
  font-size: 0.75rem;
  color: #64748B;
  font-weight: 600;
}

.rm-time-val {
  font-size: 0.8125rem;
  font-family: monospace;
}

.rm-tech-note {
  font-size: 0.725rem;
  color: #64748B;
  line-height: 1.5;
  background: #EFF6FF;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid #DBEAFE;
}

.rm-tech-note code {
  background: #DBEAFE;
  color: #082C69;
  padding: 1px 4px;
  border-radius: 4px;
  font-family: monospace;
}

/* Badges */
.rm-code-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.775rem;
  background: #EFF6FF;
  color: #082C69;
  border: 1px solid #DBEAFE;
  padding: 2px 7px;
  border-radius: 6px;
}

.rm-stops-pill {
  font-size: 0.7rem;
  font-weight: 600;
  background: #F1F5F9;
  color: #64748B;
  padding: 2px 7px;
  border-radius: 6px;
}

.rm-days-tag {
  display: inline-block;
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #FDE68A;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.725rem;
  font-weight: 600;
}

.text-navy {
  color: #082C69;
}

.rm-stat-pill {
  font-size: 0.725rem;
  font-weight: 600;
  background: #F1F5F9;
  color: #334155;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #E2E8F0;
  display: inline-flex;
  align-items: center;
}

.rm-terminal-strip {
  background: #F8FAFC;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px dashed #CBD5E1;
  font-size: 0.75rem;
  color: #082C69;
  font-weight: 600;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* Custom Subscription Toggle Switches */
.rm-toggle-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.rm-toggle-row:hover:not(.is-disabled) {
  border-color: #CBD5E1;
  background: #F8FAFC;
}

.rm-toggle-row.is-active {
  border-color: #BFDBFE;
  background: #F8FAFF;
}

.rm-toggle-row.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #F1F5F9;
}

.rm-toggle-switch {
  flex-shrink: 0;
}

.rm-toggle-track {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: #CBD5E1;
  position: relative;
  transition: background 0.25s ease;
}

.rm-toggle-row.is-active .rm-toggle-track {
  background: #082C69;
}

.rm-toggle-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #FFFFFF;
  position: absolute;
  top: 3px;
  right: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.rm-toggle-row.is-active .rm-toggle-thumb {
  transform: translateX(-20px);
}

.rm-toggle-text {
  display: flex;
  flex-direction: column;
}

.rm-toggle-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1E293B;
  line-height: 1.3;
}

.rm-toggle-desc {
  font-size: 0.775rem;
  color: #64748B;
  line-height: 1.4;
  margin-top: 2px;
}
</style>
